/* eslint-disable no-restricted-imports */
import React, {useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton/index.ts'
import {
    UIGameResourceCardsTooltipContent,
    type UIGameResourceCardsTooltipContentHandle,
} from '../../../common/UIGameResourceCardsTooltipContent/index.ts'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PieceTypes} from '@colonist/game-base-types'
import {useActionButtonCommonErrors} from '../utils.ts'
import {CITY_WALL_IMAGE_PATH} from './utils.ts'
import {useGameStore} from '../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators.ts'
import {ActionState} from '@colonist/game-states'
import {useMechanicValidatorCityWall} from '../../../store/selectors/useMechanicValidatorCityWall.ts'
import {type TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator.ts'

const cardsToBuildCityWall = getCardsForPiece(PieceTypes.CityWall)

export const UIGameActionButtonCityWall = ({
    className,
}: {
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const currentStateValidator = useCurrentStateValidator()
    const {getPlayerValidator} = usePlayerValidators()
    const playerValidator = getPlayerValidator(myColor)
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const {sendBuildCityWall, sendCancelAction, sendClearCornerHighlight} = useUIGameSocketContext()
    const cityWallValidator = useMechanicValidatorCityWall()
    const actionStatus = useMemo(() => {
        if(!cityWallValidator) {
            return {canTakeAction: false}
        }
        const canPlaceCityWallActionStatus = cityWallValidator.canPlayerPlaceCityWall(myColor)

        return canPlaceCityWallActionStatus
    }, [cityWallValidator, myColor])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToBuildCityWall)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const disabled = hasCommonErrors || !actionStatus.canTakeAction
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction
    const icon = CITY_WALL_IMAGE_PATH[myColor]
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isPlacingCityWall = actionState === ActionState.PlaceCityWall && !disabled
    const bankCityWallAmount = cityWallValidator?.getCityWallPlayerState(myColor).bankCityWallAmount ?? 0

    const onClick = () => {
        if(disabled) return
        if(isPlacingCityWall) {
            sendClearCornerHighlight()
            sendCancelAction()
        } else {
            sendBuildCityWall()
        }
    }

    return <UIGameActionButton
        className={className}
        count={bankCityWallAmount}
        countColor={myColor}
        disabled={disabled}
        helperText={helperText}
        highlight={isPlacingCityWall}
        iconPath={icon}
        onClickActionIfDisabled={triggerFlickerCardsAnimation}
        tooltipCustomMenuComponent={
            <UIGameResourceCardsTooltipContent
                cardInventory={playerValidator.resourceCards.cards}
                cardsToBuild={cardsToBuildCityWall}
                ref={tooltipRef}/>
        }
        onClick={onClick}
    />
}
